/**
 * BirthdayCelebration — an on-brand sacred birthday moment that appears once
 * per year on the user's birthday. Not a generic confetti party — instead a
 * cosmic, ancestral, violet-fuchsia-gold ceremony with floating sparkles and
 * a personalized blessing.
 *
 * Triggered by GET /api/birthday/today — only renders when is_birthday === true.
 * Shown ONCE per year (tracked in localStorage by the year string).
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// On-brand birthday blessings — rotated per user so two people born the
// same day see slightly different (but equally sacred) wording.
const BLESSINGS = [
  "Today the universe whispers your name back to you. Receive it slowly.",
  "Another full revolution around the sun, and you're still becoming. That's holy.",
  "Your ancestors are clapping somewhere. Their dreams have your face.",
  "The earth was waiting for the day you'd return to your own birthday — soft, anchored, free.",
  "You are the prayer your lineage spoke into the dark. Today, the dark answers back.",
  "May this year root you deeper and lift you higher in the same breath.",
  "The version of you arriving this year has been preparing in secret. Welcome them in.",
  "Today is sacred because YOU are sacred. No further explanation required.",
];

function pickBlessing(name = "") {
  let h = 0;
  const seed = `${name}-${new Date().getFullYear()}`;
  for (let i = 0; i < seed.length; i++) h = (h + seed.charCodeAt(i)) % 9973;
  return BLESSINGS[h % BLESSINGS.length];
}

// Soft floating particles — gentle, not a cartoon explosion.
function Particles() {
  const items = useMemo(
    () =>
      Array.from({ length: 24 }).map((_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 6,
        duration: 8 + Math.random() * 6,
        size: 4 + Math.random() * 8,
        opacity: 0.35 + Math.random() * 0.45,
        // Brand palette — violet, fuchsia, amber/gold (no rainbow)
        color: ["#a78bfa", "#e879f9", "#fbbf24", "#f472b6", "#c084fc"][i % 5],
      })),
    []
  );

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {items.map((p) => (
        <span
          key={p.id}
          className="absolute rounded-full birthday-float"
          style={{
            left: `${p.left}%`,
            bottom: "-8px",
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            opacity: p.opacity,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}66`,
          }}
        />
      ))}
    </div>
  );
}

export default function BirthdayCelebration() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null); // { is_birthday, name, age }
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/birthday/today`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.data?.is_birthday) {
          // Only show once per year per user. Key includes year so it
          // automatically resets for next year's birthday.
          const seenKey = `birthday_celebrated_${user?.id || "u"}_${new Date().getFullYear()}`;
          if (!localStorage.getItem(seenKey)) {
            setData(res.data);
            setOpen(true);
            localStorage.setItem(seenKey, "1");
          }
        }
      } catch {
        /* silent — birthday is non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, [token, user?.id]);

  if (!open || !data) return null;

  const blessing = pickBlessing(data.name || user?.name || "Soul");

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 birthday-overlay"
      data-testid="birthday-celebration"
      role="dialog"
      aria-label="Birthday celebration"
    >
      {/* Backdrop with subtle radial wash, NOT a flat black */}
      <div className="absolute inset-0 bg-gradient-radial from-violet-900/70 via-fuchsia-900/60 to-black/85 backdrop-blur-md" />

      <Particles />

      <div className="relative max-w-md w-full">
        {/* Outer glow ring */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-violet-500/40 via-fuchsia-500/40 to-amber-400/30 blur-2xl" />

        <div className="relative rounded-3xl bg-gradient-to-br from-violet-950/95 via-fuchsia-950/90 to-slate-950/95 border border-amber-300/30 p-8 text-center shadow-2xl birthday-card-rise">
          <button
            onClick={() => setOpen(false)}
            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition"
            aria-label="Close"
            data-testid="birthday-close"
          >
            <X className="w-4 h-4 text-white/60" />
          </button>

          {/* Halo */}
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-300 via-fuchsia-400 to-violet-500 blur-md birthday-halo" />
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-amber-300 via-fuchsia-500 to-violet-600 flex items-center justify-center text-4xl">
              ✨
            </div>
          </div>

          <p className="text-[11px] uppercase tracking-[0.3em] text-amber-300/90 font-semibold mb-2">
            A sacred day
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-1"
            style={{ fontFamily: "'Righteous', cursive" }}
            data-testid="birthday-greeting"
          >
            Happy Birthday,
          </h2>
          <h3
            className="text-3xl md:text-4xl bg-gradient-to-r from-amber-300 via-fuchsia-300 to-violet-300 bg-clip-text text-transparent mb-4"
            style={{ fontFamily: "'Righteous', cursive" }}
          >
            {data.name || user?.name || "Soul"}
          </h3>

          {data.age != null && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-200 text-xs font-semibold mb-5">
              <Sparkles className="w-3 h-3" />
              {data.age} trips around the sun
            </div>
          )}

          <p className="text-sm md:text-base text-white/85 italic leading-relaxed mb-6 px-2">
            "{blessing}"
          </p>

          <Button
            onClick={() => setOpen(false)}
            className="w-full bg-gradient-to-r from-amber-400 via-fuchsia-500 to-violet-500 hover:opacity-95 text-white font-semibold rounded-full py-5 shadow-lg"
            data-testid="birthday-receive-btn"
          >
            I receive it 🌟
          </Button>

          <p className="text-[10px] text-white/40 mt-4 tracking-wider uppercase">
            From all of Lala Land
          </p>
        </div>
      </div>

      {/* Inline animations — kept local so this component is self-contained */}
      <style>{`
        @keyframes birthday-float {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(-120vh) translateX(20px); opacity: 0; }
        }
        .birthday-float { animation-name: birthday-float; animation-iteration-count: infinite; animation-timing-function: ease-out; }

        @keyframes birthday-card-rise {
          0%   { transform: translateY(20px) scale(0.96); opacity: 0; }
          60%  { opacity: 1; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        .birthday-card-rise { animation: birthday-card-rise 700ms cubic-bezier(0.22, 1, 0.36, 1) both; }

        @keyframes birthday-halo {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50%      { opacity: 0.85; transform: scale(1.08); }
        }
        .birthday-halo { animation: birthday-halo 3.5s ease-in-out infinite; }

        @keyframes birthday-overlay-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .birthday-overlay { animation: birthday-overlay-in 500ms ease-out both; }

        .bg-gradient-radial { background: radial-gradient(circle at center, var(--tw-gradient-from), var(--tw-gradient-via) 50%, var(--tw-gradient-to) 100%); }
      `}</style>
    </div>
  );
}
